<template>
  <v-card variant="flat" class="pa-4 ncrm-pbx-calls">
    <div class="d-flex flex-wrap align-center ga-3 mb-3">
      <div>
        <strong>{{ $t("ncrm.integrations.pbx.calls.title") }}</strong>
        <div class="text-caption text-medium-emphasis">{{ $t("ncrm.integrations.pbx.calls.subtitle") }}</div>
      </div>
      <v-spacer />
      <v-select
        :model-value="statusFilter"
        class="ncrm-pbx-calls__filter"
        density="compact"
        hide-details
        :items="callStatusOptions"
        @update:model-value="$emit('status-change', $event)"
      />
    </div>

    <v-data-table
      :headers="callHeaders"
      :items="calls"
      :loading="loading"
      item-value="id"
      density="comfortable"
      :row-props="rowProps"
    >
      <template #[`item.time_start`]="{ item }">{{ formatDate(item.time_start) }}</template>
      <template #[`item.call_status`]="{ item }">
        <v-chip size="x-small" v-bind="statusMeta(item.call_status)">{{ statusLabel(item.call_status) }}</v-chip>
      </template>
      <template #[`item.match_status`]="{ item }">
        <v-chip size="x-small" v-bind="statusMeta(item.match_status)">{{ statusLabel(item.match_status) }}</v-chip>
      </template>
      <template #[`item.duration_seconds`]="{ item }">{{ formatDuration(item.duration_seconds) }}</template>
      <template #[`item.actions`]="{ item }">
        <v-btn v-if="hasRecording(item)" icon variant="text" size="small" :loading="downloadingId === item.id" :title="$t('ncrm.integrations.pbx.actions.downloadRecording')" @click="$emit('download', item)">
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <v-btn v-if="isOpenable(item)" icon variant="text" size="small" :title="$t('ncrm.integrations.pbx.actions.open')" @click="emitOpen(item)">
          <v-icon>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn icon variant="text" size="small" :title="$t('ncrm.integrations.pbx.actions.raw')" @click="$emit('raw', item)">
          <v-icon>mdi-code-json</v-icon>
        </v-btn>
        <v-btn v-if="canLink(item)" icon variant="text" size="small" :title="$t('ncrm.integrations.pbx.actions.link')" @click="$emit('link', item)">
          <v-icon>mdi-link-variant</v-icon>
        </v-btn>
      </template>
      <template #no-data>
        <div class="pa-6 text-medium-emphasis">{{ $t("ncrm.integrations.pbx.calls.empty") }}</div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { formatPbxDateTime, formatPbxDuration, resolvePbxStatusMeta } from "@/Utile/ncrmPbx.mjs";

export default {
  name: "NcrmPbxCallsTable",
  props: {
    calls: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    statusFilter: { type: String, default: "unmatched" },
    downloadingId: { type: [Number, String], default: null },
  },
  emits: ["status-change", "raw", "link", "open-entity", "download"],
  computed: {
    callStatusOptions() {
      return [
        { title: this.$t("ncrm.integrations.pbx.calls.filters.unmatched"), value: "unmatched" },
        { title: this.$t("ncrm.integrations.pbx.calls.filters.matched"), value: "matched" },
        { title: this.$t("ncrm.integrations.pbx.calls.filters.linked"), value: "linked_manual" },
        { title: this.$t("ncrm.integrations.pbx.calls.filters.all"), value: "" },
      ];
    },
    callHeaders() {
      return [
        { title: this.$t("ncrm.integrations.pbx.calls.headers.time"), key: "time_start" },
        { title: this.$t("ncrm.integrations.pbx.calls.headers.from"), key: "call_from_number" },
        { title: this.$t("ncrm.integrations.pbx.calls.headers.to"), key: "call_to_number" },
        { title: this.$t("ncrm.integrations.pbx.calls.headers.duration"), key: "duration_seconds" },
        { title: this.$t("ncrm.integrations.pbx.calls.headers.status"), key: "call_status" },
        { title: this.$t("ncrm.integrations.pbx.calls.headers.match"), key: "match_status" },
        { title: "", key: "actions", sortable: false, align: "end" },
      ];
    },
  },
  methods: {
    statusMeta(status) {
      return resolvePbxStatusMeta(status);
    },
    statusLabel(value) {
      return this.$t(
        `ncrm.integrations.pbx.status.${String(value || "unknown").replace(/\s+/g, "_").toLowerCase()}`,
        String(value || "-")
      );
    },
    formatDate: formatPbxDateTime,
    formatDuration: formatPbxDuration,
    canLink(item) {
      return String(item?.match_status || "").toLowerCase() === "unmatched" && !item?.activity_id;
    },
    hasRecording(item) {
      return Number(item?.recording_id) > 0;
    },
    // Un apel este deschidibil dacă a fost matchuit (automat sau manual) la o
    // entitate care are pagină de detaliu în CRM (company/person/deal).
    isOpenable(item) {
      const type = String(item?.matched_entity_type || "").toLowerCase();
      return ["company", "person", "deal"].includes(type) && Number(item?.matched_entity_id) > 0;
    },
    emitOpen(item) {
      if (!this.isOpenable(item)) return;
      this.$emit("open-entity", {
        type: String(item.matched_entity_type).toLowerCase(),
        id: Number(item.matched_entity_id),
      });
    },
    rowProps({ item }) {
      if (!this.isOpenable(item)) return {};
      return {
        class: "ncrm-pbx-calls__row--openable",
        title: this.$t("ncrm.integrations.pbx.calls.openHint"),
        onDblclick: () => this.emitOpen(item),
      };
    },
  },
};
</script>

<style scoped>
.ncrm-pbx-calls {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}

.ncrm-pbx-calls__filter {
  max-width: 210px;
}

.ncrm-pbx-calls :deep(.ncrm-pbx-calls__row--openable) {
  cursor: pointer;
}

.ncrm-pbx-calls :deep(.ncrm-pbx-calls__row--openable:hover) {
  background: var(--ncrm-color-hover-soft);
}
</style>
